import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Collapse,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import InlineAlert from '../../../shared/components/InlineAlert';
import EmptyStateMessage from '../../../shared/components/EmptyStateMessage';
import { EntityListPanel } from '../../../shared/components/EntityListPanel';
import type { EntityListItemConfig } from '../../../shared/components/EntityListPanel';
import DeleteConfirmDialog from '../../../shared/components/DeleteConfirmDialog';
import { useServiceComponents } from '../hooks/useServiceComponents';
import { useCatalogService } from '../services/catalogService';
import type { ServiceComponentSummary } from '../services/catalogService';
import ServiceComponentEditForm from './ServiceComponentEditForm';
import BindingEditor from '../../service-offerings/admin/BindingEditor';
import type { Portfolio } from '../services/catalogService';

interface ServiceComponentManagementPanelProps {
  portfolios: Portfolio[];
  isOpen: boolean;
  onCountChange?: (count: number) => void;
}

const componentListConfig: EntityListItemConfig<ServiceComponentSummary> = {
  getId: (p) => p.id,
  getPrimaryText: (p) => p.name,
  getSecondaryText: (p) => p.portfolio || 'No portfolio',
  getSearchText: (p) => [p.name, p.service, p.portfolio].join(' '),
  secondaryTypographyProps: { variant: 'caption' },
};

const componentGroupBy = (p: ServiceComponentSummary) => [{ key: p.service, label: p.service }];

const ServiceComponentManagementPanel: React.FC<ServiceComponentManagementPanelProps> = ({
  portfolios,
  isOpen,
  onCountChange,
}) => {
  const {
    entityManagement,
    handleSelect,
    showRenameWarning,
    dependsOnOptions,
    originalName,
    productDetail,
    refreshServiceComponentDetail,
  } = useServiceComponents(isOpen);

  const catalogService = useCatalogService();
  const [versionBusy, setVersionBusy] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [expandedVersion, setExpandedVersion] = useState<string | null>(null);

  const {
    entities,
    selectedEntity,
    formData,
    saving,
    loading,
    error,
    successMessage,
    showDeleteConfirm,
    handleSave,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleChange,
    setError,
    hasChanges,
    fieldErrors,
  } = entityManagement;

  useEffect(() => {
    onCountChange?.(entities.length);
  }, [entities.length, onCountChange]);

  // New component selected → collapse history + any opened old-version bindings.
  // The active version is always shown on its own, so nothing auto-expands here.
  useEffect(() => {
    setHistoryOpen(false);
    setExpandedVersion(null);
  }, [productDetail]);

  const handleToggleVersion = useCallback(async (versionName: string, active: boolean) => {
    if (!selectedEntity) return;
    setVersionBusy(versionName);
    try {
      if (active) {
        await catalogService.deactivateVersion(selectedEntity.id, selectedEntity.service, versionName);
      } else {
        await catalogService.activateVersion(selectedEntity.id, selectedEntity.service, versionName);
      }
      refreshServiceComponentDetail();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update version');
    } finally {
      setVersionBusy(null);
    }
  }, [selectedEntity, catalogService, refreshServiceComponentDetail, setError]);

  const showForm = !!selectedEntity;

  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
      <Box sx={{ width: 320, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
        <EntityListPanel<ServiceComponentSummary>
          entities={entities}
          selectedEntity={selectedEntity}
          isNew={false}
          saving={saving}
          config={componentListConfig}
          searchable
          groupBy={componentGroupBy}
          emptyStateText="No components found"
          emptyStateHelperText="Created by the service-catalog pipeline. Run register.sh to bootstrap."
          onSelect={handleSelect}
          isLoading={loading}
        />
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', p: 3 }}>
        {error && (
          <InlineAlert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </InlineAlert>
        )}
        {successMessage && (
          <InlineAlert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </InlineAlert>
        )}

        {showForm ? (
          <>
            <ServiceComponentEditForm
              formData={formData}
              fieldErrors={fieldErrors}
              portfolios={portfolios}
              showRenameWarning={showRenameWarning}
              originalName={originalName}
              dependsOnOptions={dependsOnOptions}
              saving={saving}
              selectedEntity={selectedEntity}
              hasChanges={hasChanges}
              handleChange={handleChange}
              handleSave={handleSave}
              handleDeleteClick={handleDeleteClick}
            />

            {selectedEntity && productDetail && productDetail.versions.length > 0 && (() => {
              const activeVersion = productDetail.versions.find((v) => v.active);
              const history = productDetail.versions.filter((v) => !v.active).reverse();
              return (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    Version Bindings
                  </Typography>

                  {/* Active version — the primary content */}
                  {activeVersion ? (
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            {activeVersion.name}
                          </Typography>
                          <Typography variant="caption" color="success.main">active</Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          disabled={versionBusy === activeVersion.name}
                          onClick={() => handleToggleVersion(activeVersion.name, true)}
                        >
                          Deactivate
                        </Button>
                      </Box>
                      <BindingEditor
                        service={selectedEntity.service}
                        productName={selectedEntity.name}
                        versionName={activeVersion.name}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No active version.
                    </Typography>
                  )}

                  {/* Version history — collapsed audit trail of inactive versions */}
                  {history.length > 0 && (
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <ListItemButton onClick={() => setHistoryOpen((o) => !o)} sx={{ py: 0.75 }}>
                        <ListItemText
                          primary={
                            <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              Version history ({history.length})
                            </Typography>
                          }
                        />
                        {historyOpen ? <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
                      </ListItemButton>
                      <Collapse in={historyOpen} timeout="auto">
                        {history.map((version) => (
                          <Box key={version.name} sx={{ borderTop: 1, borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                  {version.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(version.created_time).toLocaleDateString()}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Button
                                  size="small"
                                  onClick={() => setExpandedVersion((v) => (v === version.name ? null : version.name))}
                                  sx={{ color: 'text.secondary' }}
                                >
                                  {expandedVersion === version.name ? 'Hide' : 'Bindings'}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  disabled={versionBusy === version.name}
                                  onClick={() => handleToggleVersion(version.name, false)}
                                >
                                  Activate
                                </Button>
                              </Box>
                            </Box>
                            <Collapse in={expandedVersion === version.name} timeout="auto">
                              <Box sx={{ px: 2, pb: 2 }}>
                                <BindingEditor
                                  service={selectedEntity.service}
                                  productName={selectedEntity.name}
                                  versionName={version.name}
                                />
                              </Box>
                            </Collapse>
                          </Box>
                        ))}
                      </Collapse>
                    </Box>
                  )}
                </Box>
              );
            })()}
          </>
        ) : (
          <EmptyStateMessage
            icon={InventoryIcon}
            title="No component selected"
            description="Select a component to view its details. Components are created by the service-catalog pipeline."
          />
        )}
      </Box>

      <DeleteConfirmDialog
        open={showDeleteConfirm}
        title="Archive ServiceComponent?"
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        confirmText="Archive ServiceComponent"
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          Archive component: <strong>{selectedEntity?.name}</strong>?
        </Typography>
        <InlineAlert severity="warning">
          <Typography variant="body2">
            Archived components cannot accept new artifact registrations. Existing deployments are unaffected.
          </Typography>
        </InlineAlert>
      </DeleteConfirmDialog>
    </Box>
  );
};

export default ServiceComponentManagementPanel;
